import { useCallback, useState } from 'react';
import {
  Form,
  FormSizes,
  Button,
  ButtonVariants,
  ButtonSizes,
  Alert,
  AlertVariants,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  FormValidateOn,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { MODULE_CERTIFICATE_TYPES } from '@/app/routes';
import { isFormDirty, EMPTY_LIST, EMPTY_MAP } from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import { SummaryCard } from '../SummaryCard';
import { SelectFromTable } from '../SelectFromTable';
import styles from './FulfillForm.module.scss';

/**
 * Props:
 *   children  the heading text
 */
// Not Form.Section's own `title` (caption, placeholder-grey) — this page's headings are larger.
const SectionHeading = ({ children }) => (
  <div className={styles.sectionHeading}>
    <Typography
      variant={TypographyVariants.bodySm}
      weight={TypographyWeights.semibold}
      color={TypographyColors.primary}
    >
      {children}
    </Typography>
  </div>
);

/**
 * Props:
 *   config   the slice's fulfil config — its sections, headings and General details;
 *            `alignToSummary` sizes every picker's button to the summary card's grid
 *   record   the entity being fulfilled — an order or an outgoing shipment
 *   module   the active certificate module, which scopes every section's query
 *   slots    row name → the fields that row renders; the config says which rows a section has
 *   initialValues / onSubmit / onClose / isSubmitting
 */
// Header, General details, the config's sections, footer. Nothing here knows a fulfil field.
export const FulfillForm = ({
  config,
  record,
  module,
  slots = EMPTY_MAP,
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
}) => {
  const t = useTranslations();
  const { buildGroups, noteKey, summaryColumns = 5 } = config;
  const sections = config.sections ?? EMPTY_LIST;

  // Caught here so the mutation is never called with a half-filled body.
  const [missing, setMissing] = useState(EMPTY_LIST);
  const handleSubmit = useCallback(
    (values) => {
      const unmet = (config.required ?? EMPTY_LIST).filter((rule) => rule.isMissing(values));
      setMissing(unmet.map((rule) => rule.name));
      if (unmet.length > 0) return;
      onSubmit(values);
    },
    [config.required, onSubmit],
  );

  // Each section reads the screen through this, so a config never touches a component.
  const buildContext = useCallback(
    (section, values) => ({
      record,
      module,
      certificateType: MODULE_CERTIFICATE_TYPES[module],
      tileFields: section.tileFields,
      remainingByUnit: record.remainingByUnit,
      committedByUnit: record.committedByUnit,
      pendingOrderId: section.readPendingOrderId?.(values, record),
      assigned: section.readAssigned?.(values, record),
      showMissing: missing.includes(section.name),
    }),
    [record, module, missing],
  );

  const note = noteKey ? t(noteKey) : undefined;
  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);
  const confirmDiscard = useCallback(() => {
    setConfirmingClose(false);
    onClose();
  }, [onClose]);

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values }) => {
        const requestClose = isFormDirty(values, initialValues) ? askBeforeClosing : onClose;

        return (
          <>
            <Form.Header
              title={t(config.titleKey ?? 'ACTION_FULFILL')}
              onClose={requestClose}
              closeLabel={t('COMMON_CLOSE')}
            />

            <Form.Section className={styles.body}>
              <SummaryCard groups={buildGroups(t, record, values)} columns={summaryColumns} />
              {note ? (
                <Typography
                  variant={TypographyVariants.caption}
                  color={TypographyColors.placeholder}
                >
                  {note}
                </Typography>
              ) : null}
            </Form.Section>

            {sections
              .filter((section) => section.isVisible?.(values) ?? true)
              .map((section) => (
                <Form.Section key={section.name} className={styles.body}>
                  {section.sectionHeadingKey ? (
                    <SectionHeading>{t(section.sectionHeadingKey)}</SectionHeading>
                  ) : null}

                  {(section.rows ?? EMPTY_LIST)
                    .filter((row) => row.isVisible?.(values) ?? true)
                    .map((row) => (
                      <Form.Row
                        key={row.slot}
                        columns={row.columns ?? 1}
                        className={row.layout ? styles[row.layout] : undefined}
                      >
                        {slots[row.slot]}
                      </Form.Row>
                    ))}

                  {section.useCandidates && (section.pickerVisible?.(values) ?? true) ? (
                    <Form.Row columns={1}>
                      <SelectFromTable
                        config={section}
                        context={buildContext(section, values)}
                        disabled={section.isDisabled?.(record, values)}
                        promptClassName={config.alignToSummary ? styles.anchoredPrompt : undefined}
                        note={
                          section.isDisabled?.(record, values) && section.disabledNoteKey
                            ? t(section.disabledNoteKey)
                            : undefined
                        }
                      />
                    </Form.Row>
                  ) : null}

                  {missing.includes(section.name) && section.missingKey ? (
                    <Alert variant={AlertVariants.error}>{t(section.missingKey)}</Alert>
                  ) : null}
                </Form.Section>
              ))}

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {t('COMMON_SAVE')}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={confirmDiscard}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};
